import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, Gavel, Share2, Copy, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const PRINT_CSS = `
  @media print {
    @page { size: A4; margin: 18mm 16mm; }
    html, body { background: #ffffff !important; color: #111 !important; }
    .no-print, header[data-testid='app-header'], footer, nav { display: none !important; }
    .board-book-page { color: #111 !important; background: #ffffff !important; box-shadow: none !important; }
    .board-book-page * { color: inherit !important; }
    .board-book-page .accent { color: #8a6c1f !important; }
    .board-book-page .rule { border-color: #ccc !important; }
    .board-book-section { break-inside: avoid; page-break-inside: avoid; }
    .board-book-cover { break-after: page; page-break-after: always; }
  }
  .board-book-page { font-family: 'Fraunces', 'Cormorant Garamond', 'Georgia', serif; }
`;

const QUARTER_MONTHS = { 1: "January – March", 2: "April – June", 3: "July – September", 4: "October – December" };

export function StatRow({ stats }) {
  const cells = [
    ["Sessions held", stats.sessions_held],
    ["Weeks in session", stats.weeks_held],
    ["Minutes on record", stats.minutes_on_the_record],
    ["Held on time", stats.on_time_rate == null ? "—" : `${stats.on_time_rate}%`],
    ["Commitments made", stats.items_created ?? "—"],
    ["Commitments kept", stats.completion_rate == null ? "—" : `${stats.completion_rate}%`],
    ["Held late", stats.held_late ?? 0],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-5 gap-x-6 sm:gap-x-8 border-y rule border-[#ddd] py-8 my-10" data-testid="quarter-stat-row">
      {cells.map(([label, value]) => (
        <div key={label}>
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] accent leading-tight min-h-[26px] sm:min-h-0" style={{ color: "#8a6c1f" }}>{label}</p>
          <p className="text-2xl sm:text-3xl mt-1">{value}</p>
        </div>
      ))}
    </div>
  );
}

export function Section({ eyebrow, title, children }) {
  return (
    <section className="board-book-section mt-12">
      <p className="text-[10px] font-mono uppercase tracking-[0.25em] accent" style={{ color: "#8a6c1f" }}>{eyebrow}</p>
      <h2 className="text-2xl mt-2 mb-4">{title}</h2>
      {children}
    </section>
  );
}

export function ReviewBody({ review }) {
  return (
    <>
      {review.executive_summary && (
        <Section eyebrow="§ 1 · Executive summary" title="The quarter, in one read">
          <p className="text-base leading-relaxed">{review.executive_summary}</p>
        </Section>
      )}
      {Array.isArray(review.themes) && review.themes.length > 0 && (
        <Section eyebrow="§ 2 · Themes" title="What this quarter was actually about">
          <div className="space-y-5">
            {review.themes.map((t, i) => (
              <div key={i}>
                <h3 className="text-lg">{t.title}</h3>
                <p className="text-base leading-relaxed mt-1">{t.detail}</p>
              </div>
            ))}
          </div>
        </Section>
      )}
      {Array.isArray(review.wins) && review.wins.length > 0 && (
        <Section eyebrow="§ 3 · Wins" title="Shipped, closed, banked">
          <ul className="space-y-2 list-disc pl-5">
            {review.wins.map((w, i) => <li key={i} className="text-base leading-relaxed">{w}</li>)}
          </ul>
        </Section>
      )}
      {Array.isArray(review.misses) && review.misses.length > 0 && (
        <Section eyebrow="§ 4 · Misses" title="Named, not buried">
          <ul className="space-y-2 list-disc pl-5">
            {review.misses.map((w, i) => <li key={i} className="text-base leading-relaxed">{w}</li>)}
          </ul>
        </Section>
      )}
      {review.pattern_analysis && (
        <Section eyebrow="§ 5 · Pattern" title="The throughline">
          <p className="text-base leading-relaxed">{review.pattern_analysis}</p>
        </Section>
      )}
      {Array.isArray(review.next_quarter_bets) && review.next_quarter_bets.length > 0 && (
        <Section eyebrow="§ 6 · Next quarter" title="Three bets">
          <ol className="space-y-3 list-decimal pl-5">
            {review.next_quarter_bets.map((b, i) => <li key={i} className="text-base leading-relaxed">{b}</li>)}
          </ol>
        </Section>
      )}
      {review.chair_note && (
        <section className="board-book-section mt-14 border-t rule border-[#ddd] pt-8">
          <p className="text-base italic leading-relaxed">{review.chair_note}</p>
        </section>
      )}
    </>
  );
}

/** Quarter Review — a whole quarter of sessions compiled into one Board Book. */
export default function QuarterReview() {
  const { year, quarter } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compiling, setCompiling] = useState(false);
  const [chair, setChair] = useState(null);
  const [shareToken, setShareToken] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    const [r, me] = await Promise.all([
      api.get(`/quarter-review/${year}/${quarter}`),
      api.get("/me").catch(() => ({ data: null })),
    ]);
    setData(r.data);
    setChair(me.data);
    setShareToken(r.data.share?.token || null);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!cancelled) await load();
      } catch (e) {
        console.debug("quarter review load failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, quarter]);

  const compile = async () => {
    setCompiling(true);
    try {
      await api.post(`/quarter-review/${year}/${quarter}/compile`);
      await load();
      toast.success("Quarter compiled.");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not compile the quarter.");
    } finally {
      setCompiling(false);
    }
  };

  const share = async () => {
    try {
      const r = await api.post(`/quarter-review/${year}/${quarter}/share`);
      setShareToken(r.data.token);
      const url = `${window.location.origin}/quarter/${r.data.token}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Public link copied.");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not create a link.");
    }
  };

  if (loading) return <div className="min-h-[40vh] flex items-center justify-center text-[#8a8a8a]">Loading quarter…</div>;
  if (!data) return <div className="min-h-[40vh] flex items-center justify-center text-[#8a8a8a]">Not found.</div>;

  const { stats, review, sessions } = data;
  const chairName = chair?.name || (chair?.email ? chair.email.split("@")[0] : null) || "The Chair";

  return (
    <>
      <style>{PRINT_CSS}</style>

      <div className="no-print flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-3">
        <Link to="/board-books" className="text-xs text-[#8a8a8a] hover:text-[#c89f45] flex items-center gap-1" data-testid="quarter-back">
          <ArrowLeft size={12} /> Board Books
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          {review ? (
            <>
              <button
                type="button"
                onClick={share}
                className="inline-flex items-center gap-2 border border-[#2a2a2a] hover:border-[#c89f45] text-[#eaeaea] hover:text-[#c89f45] rounded-md px-4 py-3 sm:py-2 text-sm transition-colors"
                data-testid="quarter-share"
              >
                {copied ? <Check size={14} /> : shareToken ? <Copy size={14} /> : <Share2 size={14} />}
                {shareToken ? "Copy public link" : "Share"}
              </button>
              <button
                type="button"
                onClick={compile}
                disabled={compiling}
                className="inline-flex items-center gap-2 border border-[#2a2a2a] hover:border-[#c89f45] text-[#eaeaea] hover:text-[#c89f45] rounded-md px-4 py-3 sm:py-2 text-sm transition-colors disabled:opacity-50"
                data-testid="quarter-recompile"
              >
                <Sparkles size={14} /> {compiling ? "Recompiling…" : "Regenerate"}
              </button>
              <button
                type="button"
                onClick={() => window.print()}
                className="inline-flex items-center gap-2 bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] font-medium rounded-md px-4 py-3 sm:py-2 text-sm transition-colors"
                data-testid="quarter-print"
              >
                <Printer size={14} /> Print / Save as PDF
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={compile}
              disabled={compiling || stats.sessions_held === 0}
              className="inline-flex items-center gap-2 bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] font-medium rounded-md px-4 py-3 sm:py-2 text-sm transition-colors disabled:opacity-50"
              data-testid="quarter-compile"
            >
              <Sparkles size={14} /> {compiling ? "Compiling the quarter…" : "Compile the quarter"}
            </button>
          )}
        </div>
      </div>

      <article className="board-book-page max-w-3xl mx-auto bg-white text-[#111] rounded-md shadow-2xl p-6 sm:p-10 lg:p-14 print:p-0 print:shadow-none" data-testid="quarter-review-page">
        <section className="board-book-cover text-center py-10">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-sm border border-[#c89f45] mb-6">
            <Gavel size={22} className="accent" style={{ color: "#8a6c1f" }} />
          </div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] accent" style={{ color: "#8a6c1f" }}>
            Board Book · Quarter review
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight">
            {review?.headline || `Q${quarter} ${year}`}
          </h1>
          <p className="text-sm text-[#555] mt-4 font-mono">
            Q{quarter} {year} · {QUARTER_MONTHS[Number(quarter)]}
          </p>
          <p className="text-sm text-[#555] mt-1 font-mono">Chair · {chairName}</p>
        </section>

        <StatRow stats={stats} />

        {review ? <ReviewBody review={review} /> : (
          <div className="text-center py-10" data-testid="quarter-not-compiled">
            <p className="text-lg">
              {stats.sessions_held > 0
                ? `${stats.sessions_held} session${stats.sessions_held === 1 ? "" : "s"} are on the record for this quarter.`
                : "No sessions on the record for this quarter yet."}
            </p>
            {stats.sessions_held > 0 && (
              <p className="text-sm text-[#555] mt-2">
                Press “Compile the quarter” to roll them into one Board Book.
              </p>
            )}
          </div>
        )}

        <Section eyebrow="§ Appendix" title="Sessions in this quarter">
          {sessions.length === 0 ? (
            <p className="text-base text-[#555]">None.</p>
          ) : (
            <ol className="space-y-2 list-decimal pl-5">
              {sessions.map((s) => (
                <li key={s.id} className="text-base" data-testid={`quarter-session-${s.id}`}>
                  {s.title}{" "}
                  <span className="text-[#666] font-mono text-xs">· {new Date(s.held_on).toLocaleDateString()}</span>
                  {s.days_late >= 1 && (
                    <span className="text-[#8a6c1f] font-mono text-xs" data-testid={`quarter-session-late-${s.id}`}>
                      {` · ${s.days_late}d late`}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          )}
        </Section>
      </article>
    </>
  );
}
